import { GameState } from "../core/types";

const STORAGE_KEYS = {
  CURRENT_LEVEL: "bottlePuzzle_currentLevel",
  LEVEL_PROGRESS: "bottlePuzzle_levelProgress",
  GAME_STATE: "bottlePuzzle_gameState",
  SETTINGS: "bottlePuzzle_settings"
};

export interface LevelProgress {
  level: number;
  completed: boolean;
  bestScore: number;
  bestMoves: number;
  unlocked: boolean;
}

export interface Settings {
  soundEnabled: boolean;
  musicEnabled: boolean;
  vibrationEnabled: boolean;
}

const DEFAULT_SETTINGS: Settings = {
  soundEnabled: true,
  musicEnabled: true,
  vibrationEnabled: true
};

export function getCurrentLevel(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CURRENT_LEVEL);
    return saved ? parseInt(saved, 10) : 1;
  } catch (error) {
    console.error("Failed to get current level:", error);
    return 1;
  }
}

export function setCurrentLevel(level: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.CURRENT_LEVEL, level.toString());
  } catch (error) {
    console.error("Failed to set current level:", error);
  }
}

export function getLevelProgress(level: number): LevelProgress | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.LEVEL_PROGRESS);
    if (!saved) return null;

    const progress: Record<number, LevelProgress> = JSON.parse(saved);
    return progress[level] || null;
  } catch (error) {
    console.error("Failed to get level progress:", error);
    return null;
  }
}

export function setLevelProgress(level: number, progress: LevelProgress): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.LEVEL_PROGRESS);
    const allProgress: Record<number, LevelProgress> = saved ? JSON.parse(saved) : {};
    
    allProgress[level] = progress;
    localStorage.setItem(STORAGE_KEYS.LEVEL_PROGRESS, JSON.stringify(allProgress));
  } catch (error) {
    console.error("Failed to set level progress:", error);
  }
}

export function updateLevelProgress(level: number, score: number, moves: number): void {
  try {
    const existing = getLevelProgress(level);
    
    const progress: LevelProgress = existing || {
      level,
      completed: false,
      bestScore: 0,
      bestMoves: 0,
      unlocked: level === 1
    };

    if (score > progress.bestScore) {
      progress.bestScore = score;
    }

    if (progress.bestMoves === 0 || moves < progress.bestMoves) {
      progress.bestMoves = moves;
    }

    progress.completed = true;
    progress.unlocked = true;

    setLevelProgress(level, progress);

    const nextLevel = level + 1;
    const nextProgress = getLevelProgress(nextLevel);
    if (!nextProgress || !nextProgress.unlocked) {
      setLevelProgress(nextLevel, {
        level: nextLevel,
        completed: false,
        bestScore: 0,
        bestMoves: 0,
        unlocked: true
      });
    }
  } catch (error) {
    console.error("Failed to update level progress:", error);
  }
}

export function saveGameState(state: GameState): void {
  try {
    localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(state));
  } catch (error) {
    console.error("Failed to save game state:", error);
  }
}

export function loadGameState(): GameState | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error("Failed to load game state:", error);
    return null;
  }
}

export function clearGameState(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
  } catch (error) {
    console.error("Failed to clear game state:", error);
  }
}

export function getSettings(): Settings {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : { ...DEFAULT_SETTINGS };
  } catch (error) {
    console.error("Failed to get settings:", error);
    return { ...DEFAULT_SETTINGS };
  }
}

export function setSettings(settings: Partial<Settings>): void {
  try {
    const current = getSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to set settings:", error);
  }
}

export function clearAllData(): void {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error("Failed to clear all data:", error);
  }
}

export function isStorageAvailable(): boolean {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
}
